import * as preact from "preact";

import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import AdminFeedbackRatingCells, {
  type FeedbackRatings,
} from "views/AdminFeedbackRatingCells";

// Types for props
type Props = {
  appRouter: any;
  options?: {
    bookID?: string;
  };
};

// Types for state
type State = {
  data?: BookData;
  loading: boolean;
  error?: string;
};

type BookData = {
  book: {
    _id: string;
    title: string;
    author?: string;
    language: string;
    ownerID?: string;
    public: boolean;
    hide: boolean;
    totalWords: number;
    snippet: string;
    deleted: boolean;
  };
  owner?: {
    _id: string;
    email: string;
    username: string;
    displayName?: string;
  };
  textSample: string;
  feedback: Feedback[];
  classifications: Classification[];
};

type Feedback = FeedbackRatings & {
  _id: string;
  bookID: string;
  userID: string;
  feedbackText?: string;
  language: string;
  resolved?: boolean;
  user?: {
    email: string;
    username: string;
    displayName?: string;
  };
};

type Classification = {
  _id: string;
  bookID: string;
  classificationType: string;
  result: any;
  metadata: {
    promptVersion: string;
    textHash: string;
  };
};

export default class AdminBookPage extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      data: undefined,
      loading: false,
      error: undefined,
    };
  }

  async componentDidMount() {
    await this.fetchBookData();
  }

  getBookID(): string {
    return this.props.options?.bookID || "";
  }

  async fetchBookData() {
    this.setState({ loading: true, error: undefined });

    const bookID = this.getBookID();
    if (!bookID) {
      this.setState({
        error: "No book ID provided",
        loading: false,
      });
      return;
    }

    try {
      const response = await readlangClient.request(
        "GET",
        `/api/bookAdmin/${bookID}`,
        {}
      );

      this.setState({ data: response.data, loading: false });
    } catch (error) {
      console.error("Error fetching book data:", error);
      this.setState({
        error: "Failed to load book data",
        loading: false,
      });
    }
  }

  async togglePublic() {
    if (!this.state.data) return;

    const bookID = this.getBookID();
    if (!bookID) return;

    try {
      await readlangClient.request("PUT", `/api/bookAdmin/${bookID}/public`, {
        public: !this.state.data.book.public,
      });

      await this.fetchBookData();
    } catch (error: any) {
      console.error("Error toggling public status:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to update public status");
      }
    }
  }

  async toggleHide() {
    if (!this.state.data) return;

    const bookID = this.getBookID();
    if (!bookID) return;

    try {
      await readlangClient.request("PUT", `/api/bookAdmin/${bookID}/hide`, {
        hide: !this.state.data.book.hide,
      });

      // Refresh the data
      await this.fetchBookData();
    } catch (error: any) {
      console.error("Error toggling hide status:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to update hide status");
      }
    }
  }

  async toggleFeedbackResolved(feedbackID: string, currentResolved: boolean) {
    try {
      await readlangClient.request(
        "PUT",
        `/api/bookFeedback/admin/${feedbackID}/resolve`,
        {
          resolved: !currentResolved,
        }
      );

      // Refresh the data
      await this.fetchBookData();
    } catch (error: any) {
      console.error("Error toggling resolved status:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to update feedback status");
      }
    }
  }

  async updateLanguage(newLanguage: string) {
    if (!this.state.data) return;

    const bookID = this.getBookID();
    if (!bookID) return;

    try {
      await readlangClient.request("PUT", `/api/bookAdmin/${bookID}/language`, {
        language: newLanguage,
      });

      // Refresh the data
      await this.fetchBookData();
    } catch (error: any) {
      console.error("Error updating language:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to update language");
      }
    }
  }

  renderFeedback(feedback: Feedback) {
    const timestamp = new Date(
      parseInt(feedback._id.substring(0, 8), 16) * 1000
    );

    return (
      <tr key={feedback._id} class={cn({ resolved: feedback.resolved })}>
        <td>
          {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
        </td>
        <td>
          {feedback.user ? (
            <div>
              <div>{feedback.user.email}</div>
              <div class="secondaryText">{feedback.userID}</div>
            </div>
          ) : (
            feedback.userID
          )}
        </td>
        <AdminFeedbackRatingCells feedback={feedback} />
        <td>{feedback.feedbackText || <em class="emptyText">None</em>}</td>
        <td class="center">
          <button
            onClick={() =>
              this.toggleFeedbackResolved(
                feedback._id,
                feedback.resolved || false
              )
            }
            class={cn({
              prominentGreen: !feedback.resolved,
            })}
          >
            {feedback.resolved ? "Mark Unresolved" : "Mark Resolved"}
          </button>
        </td>
      </tr>
    );
  }

  renderClassification(classification: Classification) {
    const timestamp = new Date(
      parseInt(classification._id.substring(0, 8), 16) * 1000
    );

    return (
      <tr key={classification._id}>
        <td>{classification.classificationType}</td>
        <td>
          <pre class="classificationResult">
            {JSON.stringify(classification.result, null, 2)}
          </pre>
        </td>
        <td>
          <div>Prompt v{classification.metadata.promptVersion}</div>
          <div class="secondaryText">
            {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
          </div>
        </td>
      </tr>
    );
  }

  render() {
    if (this.state.loading) {
      return <div>Loading...</div>;
    }

    if (this.state.error) {
      return (
        <div>
          <h1>
            <a href="/admin">Admin</a> - Book Details
          </h1>
          <p class="error">{this.state.error}</p>
        </div>
      );
    }

    if (!this.state.data) {
      return <div>Loading...</div>;
    }

    const { book, owner, textSample, feedback, classifications } =
      this.state.data;

    return (
      <div class="adminBookPage">
        <h1>
          <a href="/admin">Admin</a> -{" "}
          <a href="/admin/bookFeedback">Book Feedback</a> - Book Details
        </h1>

        <div class="bookDetails">
          <h2>Book Information</h2>

          <table class="infoTable">
            <tbody>
              <tr>
                <th>Title</th>
                <td>
                  <a
                    href={`/library/${book._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {book.title}
                  </a>
                </td>
              </tr>
              {book.author && (
                <tr>
                  <th>Author</th>
                  <td>{book.author}</td>
                </tr>
              )}
              <tr>
                <th>Language</th>
                <td>
                  <select
                    value={book.language}
                    onChange={(event) =>
                      this.updateLanguage(
                        (event.target as HTMLSelectElement).value
                      )
                    }
                  >
                    {languageMap.learningList.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.english} ({lang.code})
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Owner</th>
                <td>
                  {owner ? (
                    <div>
                      <a
                        href={`/user/${book.ownerID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {owner.email}
                      </a>
                      <div class="secondaryText">{book.ownerID}</div>
                    </div>
                  ) : (
                    book.ownerID || <em>No owner</em>
                  )}
                </td>
              </tr>
              <tr>
                <th>Total Words</th>
                <td>{book.totalWords}</td>
              </tr>
              <tr>
                <th>Public</th>
                <td>
                  {book.public ? "Yes" : "No"}
                  <button
                    onClick={() => this.togglePublic()}
                    class="toggleButton"
                  >
                    {book.public ? "Unshare" : "Share"}
                  </button>
                </td>
              </tr>
              <tr>
                <th>Hidden</th>
                <td>
                  {book.hide ? "Yes" : "No"}
                  <button
                    onClick={() => this.toggleHide()}
                    class="toggleButton"
                  >
                    {book.hide ? "Unhide" : "Hide"}
                  </button>
                </td>
              </tr>
              <tr>
                <th>Deleted</th>
                <td>{book.deleted ? "Yes" : "No"}</td>
              </tr>
            </tbody>
          </table>

          <h3>Text Sample</h3>
          <pre class="textSample">{textSample}</pre>
        </div>

        <div class="feedbackSection">
          <h2>Feedback ({feedback.length})</h2>
          {feedback.length === 0 ? (
            <p>No feedback for this book</p>
          ) : (
            <table class="feedbackTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Content</th>
                  <th>Language</th>
                  <th>Feedback</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>{feedback.map((fb) => this.renderFeedback(fb))}</tbody>
            </table>
          )}
        </div>

        <div class="classificationsSection">
          <h2>Classifications ({classifications.length})</h2>
          {classifications.length === 0 ? (
            <p>No classifications for this book</p>
          ) : (
            <table class="classificationsTable">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Result</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {classifications.map((c) => this.renderClassification(c))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
}
